import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtEur, fmtPct } from "@/lib/format";
import { Loader2, Trophy, TrendingUp, TrendingDown, Medal } from "lucide-react";

export default function Ranking() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.staffRanking().then((d) => { setRows(d); setLoading(false); }).catch(() => setLoading(false)); }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>;

  const maxProfit = Math.max(1, ...rows.map((r) => Math.abs(r.profit)));
  const hasData = rows.some((r) => r.revenue > 0 || r.cost > 0);
  const medal = ["text-amber-400", "text-slate-300", "text-orange-400"];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight text-slate-100">Classifica Redditività</h1>
        <p className="text-slate-500 text-sm mt-1">Chi fa guadagnare di più rispetto a quanto costa · dai report di fine evento</p>
      </div>

      {!hasData && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
          <p className="text-amber-400 font-medium">Nessun dato di redditività ancora</p>
          <p className="text-slate-400 text-sm mt-1">La classifica si popola dai <strong>Report di fine evento</strong>: assegna i turni ai tecnici nei report e qui vedrai quanto rende ciascuno rispetto al costo.</p>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={r.id} data-testid={`ranking-row-${r.id}`} className="bg-[#121824] border border-[#222D42] rounded-xl p-5 flex items-center gap-4">
            <div className="w-8 text-center shrink-0">
              {i < 3 && r.profit > 0 ? <Medal className={`w-6 h-6 mx-auto ${medal[i]}`} /> : <span className="font-mono text-slate-500 text-sm">{i + 1}</span>}
            </div>
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/20 flex items-center justify-center font-head font-bold text-amber-400 shrink-0">{r.name?.slice(0, 1).toUpperCase()}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="font-head font-semibold text-slate-100 truncate">{r.name}</p>
                <span className={`font-mono font-bold text-sm shrink-0 inline-flex items-center gap-1 ${r.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {r.profit >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />} {fmtEur(r.profit)}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-mono">
                <span>Genera {fmtEur(r.revenue)}</span>
                <span>·</span>
                <span>Costa {fmtEur(r.cost)}</span>
                <span>·</span>
                <span>{r.events} eventi · {r.turni} turni</span>
                {r.ratio > 0 && <span className={`ml-auto px-2 py-0.5 rounded-full ${r.ratio >= 1 ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>x{r.ratio.toFixed(2)}</span>}
              </div>
              <div className="w-full h-1.5 bg-[#1A2335] rounded-full overflow-hidden mt-2">
                <div className={`h-full rounded-full ${r.profit >= 0 ? "bg-emerald-500" : "bg-red-500"}`} style={{ width: `${(Math.abs(r.profit) / maxProfit) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-center text-slate-500 py-8"><Trophy className="w-10 h-10 mx-auto mb-3 text-slate-600" />Nessun membro del personale.</div>}
      </div>
    </div>
  );
}
